// frontend/src/components/projects/NewProjectModal.jsx
//
// v2: real template picker. The original modal had a 4-option dropdown
// that just toggled a couple of facets — same blank slate either way.
// This version shows the templates as visual cards with icon + tagline,
// pre-fills name/description/color/facets, and gives a proper "Custom"
// escape for power users.
//
// Why templates matter: a brand-new project is the WORST moment in the
// product. The user just told us "I want a project" and we hand them a
// blank canvas with 8 collapsed accordions. Templates anchor what the
// project IS so the user knows what to do next.

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Stack,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Card,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as ResearchIcon,
  Work as ClientIcon,
  Person as PersonalIcon,
  School as LearningIcon,
  Article as NotesIcon,
  Tune as CustomIcon,
} from '@mui/icons-material';
import { ProjectsAPI } from '../../api/projectsV2';

const VISIBILITIES = ['private', 'link', 'public'];

// Template definitions. Each one provides a complete starting state — name
// suggestion, description prompt, color, and any facets that make sense.
// Order is intentional: the most-common templates first.
const TEMPLATES = [
  {
    id: 'research',
    label: 'Research',
    icon: ResearchIcon,
    color: '#7c3aed',
    emoji: '🔬',
    tagline: 'Capture sources, voices, and notes around a topic',
    nameSuggestion: 'New research',
    descriptionSuggestion: 'What question am I trying to answer?',
    facets: { subtasks: true, deliverables: true },
  },
  {
    id: 'client',
    label: 'Client work',
    icon: ClientIcon,
    color: '#0891b2',
    emoji: '💼',
    tagline: 'Sessions, deliverables, and meeting notes for one engagement',
    nameSuggestion: 'New client engagement',
    descriptionSuggestion: 'Client name, project scope, key contacts.',
    facets: { objectives: true, deliverables: true, milestones: true },
  },
  {
    id: 'personal',
    label: 'Personal',
    icon: PersonalIcon,
    color: '#10b981',
    emoji: '🌱',
    tagline: 'A quiet space for your own thoughts and voice memos',
    nameSuggestion: 'Personal',
    descriptionSuggestion: '',
    facets: {},
  },
  {
    id: 'learning',
    label: 'Learning',
    icon: LearningIcon,
    color: '#f59e0b',
    emoji: '🎓',
    tagline: 'Notes, sources, and progress on a course or topic',
    nameSuggestion: 'New learning project',
    descriptionSuggestion: 'What am I trying to learn? What does success look like?',
    facets: { milestones: true, subtasks: true },
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: NotesIcon,
    color: '#ef4444',
    emoji: '📝',
    tagline: 'A workspace centered on text notes and voice memos',
    nameSuggestion: 'Notes',
    descriptionSuggestion: '',
    facets: {},
  },
  {
    id: 'blank',
    label: 'Custom',
    icon: CustomIcon,
    color: '#1976d2',
    emoji: '',
    tagline: 'Start with nothing — pick your own color and icon',
    nameSuggestion: '',
    descriptionSuggestion: '',
    facets: {},
  },
];

export default function NewProjectModal({ open, onClose, onCreated }) {
  const [templateId, setTemplateId] = useState('research');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#7c3aed');
  const [visibility, setVisibility] = useState('private');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = pick template, 2 = name + details

  // Reset state when modal opens
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setTemplateId('research');
    setName('');
    setDescription('');
    setColor('#7c3aed');
    setVisibility('private');
  }, [open]);

  const handlePickTemplate = (tmpl) => {
    setTemplateId(tmpl.id);
    setName(tmpl.nameSuggestion);
    setDescription(tmpl.descriptionSuggestion);
    setColor(tmpl.color);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    const tmpl = TEMPLATES.find(t => t.id === templateId) || TEMPLATES[0];
    setLoading(true);
    try {
      const { data } = await ProjectsAPI.create({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        icon: tmpl.emoji || undefined,
        visibility,
      });
      const project = data.project || data;

      // Apply template facets — non-blocking; if the user picked Custom or
      // the template has no facets, we skip the second call entirely.
      if (Object.keys(tmpl.facets).length) {
        try {
          await ProjectsAPI.update(project._id, { facets: tmpl.facets });
        } catch (e) {
          console.warn('Template facets failed to apply (project still created):', e.message);
        }
      }

      onCreated(project);
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={step === 1 ? 'sm' : 'xs'}
      fullWidth={step === 1}
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...(step === 2 && { width: 420 })
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {step === 1 ? 'Pick a template' : 'Name your project'}
          {step === 2 && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400, mt: 0.25 }}>
              {TEMPLATES.find(t => t.id === templateId)?.label}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === 1 && (
          <Box sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            mt: 1,
          }}>
            {TEMPLATES.map((tmpl) => {
              const Icon = tmpl.icon;
              return (
                <Card
                  key={tmpl.id}
                  onClick={() => handlePickTemplate(tmpl)}
                  sx={{
                    p: 1.75,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'border-color 160ms, box-shadow 160ms, transform 160ms',
                    '&:hover': {
                      borderColor: tmpl.color,
                      boxShadow: 3,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Stack spacing={1}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 1.5,
                      display: 'grid', placeItems: 'center',
                      color: tmpl.color, bgcolor: `${tmpl.color}1f`,
                    }}>
                      <Icon />
                    </Box>
                    <Typography fontWeight={700} variant="body1">{tmpl.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {tmpl.tagline}
                    </Typography>
                  </Stack>
                </Card>
              );
            })}
          </Box>
        )}

        {step === 2 && (
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField
              label="Name"
              placeholder="Project name"
              autoFocus
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.metaKey || e.ctrlKey) && handleCreate()}
              size="small"
              helperText="Press ⌘Enter to create"
            />
            <TextField
              label="Description (optional)"
              placeholder="What's this project about?"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                size="small"
                sx={{ width: 100 }}
              />
              <TextField
                select
                label="Visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              >
                {VISIBILITIES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v === 'link' ? 'Anyone with link' : v.charAt(0).toUpperCase() + v.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step === 2 && (
          <Button onClick={handleBack} disabled={loading} sx={{ mr: 'auto' }}>
            ← Back
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {step === 2 && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim() || loading}
          >
            {loading ? 'Creating...' : 'Create project'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
